import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MasterCropData, MasterLabelDiffSummary, MasterUPCData, SectionTarget } from '../types';
import { ActionMode } from './useUPCActions';
import { computeMasterLabelDiffSummary } from '../services/masterLabelState';

const VARIANT_COLORS = ['#f87171', '#60a5fa', '#34d399', '#c084fc', '#fbbf24', '#f472b6', '#2dd4bf', '#fb923c'];

interface UndoEntry {
  label: string;
  upcData: MasterUPCData[];
}

interface SelectedCropVariantInfo {
  variantId: string;
  cropId: string;
  isAlreadyRep: boolean;
}

export interface UseMasterLabelEditorOptions {
  notify: (text: string, type?: 'success' | 'warning' | 'error') => void;
}

type CropLocation =
  | { upcIdx: number; section: 'variant'; variantIdx: number; cropIdx: number }
  | { upcIdx: number; section: 'pending' | 'no-variant' | 'non-front' | 'carton' | 'inactive'; cropIdx: number };

function cloneUpcs(upcs: MasterUPCData[]): MasterUPCData[] {
  return structuredClone(upcs);
}

function makeDraftVariantId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `draft:${crypto.randomUUID()}`;
  }
  return `draft:${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function sortCrops(crops: MasterCropData[]): MasterCropData[] {
  return [...crops].sort((a, b) => {
    const aScan = a.source === 'scan' ? 0 : 1;
    const bScan = b.source === 'scan' ? 0 : 1;
    if (aScan !== bScan) return aScan - bScan;
    return a.id.localeCompare(b.id);
  });
}

function cleanupVariants(upc: MasterUPCData): void {
  upc.variants = upc.variants
    .filter((variant) => variant.crops.length > 0 || (!!variant.persistedVariantId && !!variant.verifiedBarcode));
}

function normalizeRepresentativeCrop(variant: MasterUPCData['variants'][number]): void {
  if (variant.crops.length === 0) {
    variant.representativeCropId = null;
    return;
  }
  if (!variant.crops.some((crop) => crop.id === variant.representativeCropId)) {
    variant.representativeCropId = variant.crops[0].id;
  }
}

export function useMasterLabelEditor({ notify }: UseMasterLabelEditorOptions) {
  const [baseUpcData, setBaseUpcData] = useState<MasterUPCData[]>([]);
  const [upcData, setUpcData] = useState<MasterUPCData[]>([]);
  const [selectedCropIds, setSelectedCropIds] = useState<Set<string>>(new Set());
  const [activeVariantId, setActiveVariantId] = useState<string | null>(null);
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);
  const [recentlyMovedCropIds, setRecentlyMovedCropIds] = useState<Set<string>>(new Set());

  const movedTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const lastClickedCropRef = useRef<string | null>(null);

  const mode: ActionMode = selectedCropIds.size > 0 ? 'crops-selected' : 'idle';

  useEffect(() => {
    if (mode !== 'idle') setActiveVariantId(null);
  }, [mode]);

  const diffSummary: MasterLabelDiffSummary = useMemo(
    () => computeMasterLabelDiffSummary(baseUpcData, upcData),
    [baseUpcData, upcData]
  );

  const variantColorMap = useMemo(() => {
    const map = new Map<string, string>();
    let idx = 0;
    for (const upc of upcData) {
      for (const variant of upc.variants) {
        if (!map.has(variant.id)) {
          map.set(variant.id, VARIANT_COLORS[idx % VARIANT_COLORS.length]);
          idx += 1;
        }
      }
    }
    return map;
  }, [upcData]);

  const pushUndo = useCallback((label: string) => {
    setUndoStack((prev) => [...prev, { label, upcData: cloneUpcs(upcData) }]);
  }, [upcData]);

  const undo = useCallback(() => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setUpcData(last.upcData);
      setSelectedCropIds(new Set());
      setActiveVariantId(null);
      notify(`Undid: ${last.label}`, 'warning');
      return prev.slice(0, -1);
    });
  }, [notify]);

  const markMoved = useCallback((cropIds: string[]) => {
    if (movedTimerRef.current) clearTimeout(movedTimerRef.current);
    setRecentlyMovedCropIds(new Set(cropIds));
    movedTimerRef.current = setTimeout(() => setRecentlyMovedCropIds(new Set()), 2000);
  }, []);

  const findCropLocation = useCallback((cropId: string, data: MasterUPCData[]): CropLocation | null => {
    for (let upcIdx = 0; upcIdx < data.length; upcIdx += 1) {
      const upc = data[upcIdx];
      for (let variantIdx = 0; variantIdx < upc.variants.length; variantIdx += 1) {
        const cropIdx = upc.variants[variantIdx].crops.findIndex((crop) => crop.id === cropId);
        if (cropIdx !== -1) return { upcIdx, section: 'variant', variantIdx, cropIdx };
      }
      const pendingIdx = upc.pendingCrops?.findIndex((crop) => crop.id === cropId) ?? -1;
      if (pendingIdx !== -1) return { upcIdx, section: 'pending', cropIdx: pendingIdx };
      const unassignedIdx = upc.unassignedCrops.findIndex((crop) => crop.id === cropId);
      if (unassignedIdx !== -1) return { upcIdx, section: 'no-variant', cropIdx: unassignedIdx };
      const nonFrontIdx = upc.nonFrontCrops.findIndex((crop) => crop.id === cropId);
      if (nonFrontIdx !== -1) return { upcIdx, section: 'non-front', cropIdx: nonFrontIdx };
      const cartonIdx = upc.cartonCrops.findIndex((crop) => crop.id === cropId);
      if (cartonIdx !== -1) return { upcIdx, section: 'carton', cropIdx: cartonIdx };
      const inactiveIdx = upc.inactiveCrops?.findIndex((crop) => crop.id === cropId) ?? -1;
      if (inactiveIdx !== -1) return { upcIdx, section: 'inactive', cropIdx: inactiveIdx };
    }
    return null;
  }, []);

  const removeCropById = useCallback((cropId: string, data: MasterUPCData[]): MasterCropData | null => {
    for (const upc of data) {
      for (const variant of upc.variants) {
        const idx = variant.crops.findIndex((crop) => crop.id === cropId);
        if (idx !== -1) return variant.crops.splice(idx, 1)[0];
      }
      if (upc.pendingCrops) {
        const idx = upc.pendingCrops.findIndex((crop) => crop.id === cropId);
        if (idx !== -1) return upc.pendingCrops.splice(idx, 1)[0];
      }
      {
        const idx = upc.unassignedCrops.findIndex((crop) => crop.id === cropId);
        if (idx !== -1) return upc.unassignedCrops.splice(idx, 1)[0];
      }
      {
        const idx = upc.nonFrontCrops.findIndex((crop) => crop.id === cropId);
        if (idx !== -1) return upc.nonFrontCrops.splice(idx, 1)[0];
      }
      {
        const idx = upc.cartonCrops.findIndex((crop) => crop.id === cropId);
        if (idx !== -1) return upc.cartonCrops.splice(idx, 1)[0];
      }
      if (upc.inactiveCrops) {
        const idx = upc.inactiveCrops.findIndex((crop) => crop.id === cropId);
        if (idx !== -1) return upc.inactiveCrops.splice(idx, 1)[0];
      }
    }
    return null;
  }, []);

  const insertIntoTarget = useCallback((crop: MasterCropData, target: SectionTarget, next: MasterUPCData[]) => {
    const targetItem = next.find((item) => item.itemNumber === target.itemNumber);
    if (!targetItem) return;

    crop.isInactive = false;
    crop.isPending = false;
    crop.isMismatch = false;

    if (target.type === 'variant' && target.variantId) {
      const variant = targetItem.variants.find((item) => item.id === target.variantId);
      if (variant) variant.crops.push(crop);
      return;
    }
    if (target.type === 'pending') {
      if (!targetItem.pendingCrops) targetItem.pendingCrops = [];
      crop.isInactive = true;
      crop.isPending = true;
      targetItem.pendingCrops.push(crop);
      return;
    }
    if (target.type === 'no-variant') {
      targetItem.unassignedCrops.push(crop);
      return;
    }
    if (target.type === 'non-front') {
      crop.isInactive = true;
      targetItem.nonFrontCrops.push(crop);
      return;
    }
    if (target.type === 'carton') {
      crop.isInactive = false;
      targetItem.cartonCrops.push(crop);
      return;
    }
    if (target.type === 'inactive') {
      if (!targetItem.inactiveCrops) targetItem.inactiveCrops = [];
      crop.isInactive = true;
      targetItem.inactiveCrops.push(crop);
    }
  }, []);

  const moveSelectedCrops = useCallback((target: SectionTarget) => {
    const ids = [...selectedCropIds];
    if (ids.length === 0) return;
    pushUndo(`Move ${ids.length} crop(s)`);

    setUpcData((prev) => {
      const next = cloneUpcs(prev);
      const cropsToMove: MasterCropData[] = [];
      for (const cropId of ids) {
        const crop = removeCropById(cropId, next);
        if (crop) cropsToMove.push(crop);
        for (const upc of next) {
          if (upc.differentItemCropIds?.includes(cropId)) {
            upc.differentItemCropIds = upc.differentItemCropIds.filter((id) => id !== cropId);
          }
        }
      }
      for (const crop of cropsToMove) insertIntoTarget(crop, target, next);
      for (const upc of next) {
        upc.variants.forEach((variant) => {
          variant.crops = sortCrops(variant.crops);
          normalizeRepresentativeCrop(variant);
        });
        cleanupVariants(upc);
      }
      return next;
    });

    setSelectedCropIds(new Set());
    markMoved(ids);
    notify(`Moved ${ids.length} crop${ids.length > 1 ? 's' : ''}`);
  }, [insertIntoTarget, markMoved, notify, pushUndo, removeCropById, selectedCropIds]);

  const deactivateCrops = useCallback(() => {
    const ids = [...selectedCropIds];
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    pushUndo(`Deactivate ${ids.length} crop(s)`);

    setUpcData((prev) => {
      const next = cloneUpcs(prev);
      for (const upc of next) {
        const moved: MasterCropData[] = [];
        for (const variant of upc.variants) {
          const removed = variant.crops.filter((crop) => idSet.has(crop.id));
          variant.crops = variant.crops.filter((crop) => !idSet.has(crop.id));
          moved.push(...removed);
          normalizeRepresentativeCrop(variant);
        }
        if (upc.pendingCrops) {
          moved.push(...upc.pendingCrops.filter((crop) => idSet.has(crop.id)));
          upc.pendingCrops = upc.pendingCrops.filter((crop) => !idSet.has(crop.id));
        }
        moved.push(...upc.unassignedCrops.filter((crop) => idSet.has(crop.id)));
        upc.unassignedCrops = upc.unassignedCrops.filter((crop) => !idSet.has(crop.id));
        moved.push(...upc.nonFrontCrops.filter((crop) => idSet.has(crop.id)));
        upc.nonFrontCrops = upc.nonFrontCrops.filter((crop) => !idSet.has(crop.id));
        moved.push(...upc.cartonCrops.filter((crop) => idSet.has(crop.id)));
        upc.cartonCrops = upc.cartonCrops.filter((crop) => !idSet.has(crop.id));
        upc.differentItemCropIds = (upc.differentItemCropIds ?? []).filter((cropId) => !idSet.has(cropId));
        if (!upc.inactiveCrops) upc.inactiveCrops = [];
        for (const crop of moved) {
          crop.isInactive = true;
          crop.isPending = false;
          crop.isMismatch = false;
        }
        upc.inactiveCrops.push(...moved);
        upc.inactiveCrops = sortCrops(upc.inactiveCrops);
        cleanupVariants(upc);
      }
      return next;
    });

    setSelectedCropIds(new Set());
    markMoved(ids);
    notify(`Deactivated ${ids.length} crop${ids.length > 1 ? 's' : ''}`, 'warning');
  }, [markMoved, notify, pushUndo, selectedCropIds]);

  const markDifferentItem = useCallback(() => {
    const ids = [...selectedCropIds];
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    pushUndo(`Different item ${ids.length} crop(s)`);

    setUpcData((prev) => {
      const next = cloneUpcs(prev);
      for (const upc of next) {
        for (const variant of upc.variants) {
          variant.crops = variant.crops.filter((crop) => !idSet.has(crop.id));
          normalizeRepresentativeCrop(variant);
        }
        cleanupVariants(upc);
        if (upc.pendingCrops) upc.pendingCrops = upc.pendingCrops.filter((crop) => !idSet.has(crop.id));
        upc.unassignedCrops = upc.unassignedCrops.filter((crop) => !idSet.has(crop.id));
        upc.nonFrontCrops = upc.nonFrontCrops.filter((crop) => !idSet.has(crop.id));
        upc.cartonCrops = upc.cartonCrops.filter((crop) => !idSet.has(crop.id));
        if (upc.inactiveCrops) upc.inactiveCrops = upc.inactiveCrops.filter((crop) => !idSet.has(crop.id));
        const movedIds = ids.filter((cropId) => (
          findCropLocation(cropId, prev)?.upcIdx === prev.findIndex((item) => item.itemNumber === upc.itemNumber)
        ));
        if (movedIds.length > 0) {
          upc.differentItemCropIds = sortCrops(
            [...new Set([...(upc.differentItemCropIds ?? []), ...movedIds])].map((cropId) => ({ id: cropId, imageUrl: '' }))
          ).map((crop) => crop.id);
        }
      }
      return next;
    });

    setSelectedCropIds(new Set());
    notify(`Marked ${ids.length} crop${ids.length > 1 ? 's' : ''} as different item`);
  }, [findCropLocation, notify, pushUndo, selectedCropIds]);

  const createNewVariant = useCallback((itemNumber: string) => {
    const ids = [...selectedCropIds];
    if (ids.length === 0) return;
    pushUndo(`Create variant from ${ids.length} crop(s)`);
    const draftVariantId = makeDraftVariantId();

    setUpcData((prev) => {
      const next = cloneUpcs(prev);
      const targetUpc = next.find((item) => item.itemNumber === itemNumber);
      if (!targetUpc) return prev;

      const crops: MasterCropData[] = [];
      for (const cropId of ids) {
        const crop = removeCropById(cropId, next);
        if (crop) {
          crop.isInactive = false;
          crop.isPending = false;
          crop.isMismatch = false;
          crops.push(crop);
        }
        for (const upcEntry of next) {
          upcEntry.differentItemCropIds = (upcEntry.differentItemCropIds ?? []).filter((id) => id !== cropId);
        }
      }

      for (const upcEntry of next) {
        upcEntry.variants.forEach((variant) => {
          normalizeRepresentativeCrop(variant);
        });
        cleanupVariants(upcEntry);
      }

      if (crops.length > 0) {
        targetUpc.variants.push({
          id: draftVariantId,
          persistedVariantId: null,
          itemNumber: targetUpc.itemNumber,
          representativeCropId: crops[0].id,
          barcodeStatus: 'unconfirmed',
          crops: sortCrops(crops),
        });
        targetUpc.variants.sort((a, b) => a.id.localeCompare(b.id));
      }
      return next;
    });

    setSelectedCropIds(new Set());
    markMoved(ids);
    notify('Created draft variant');
  }, [markMoved, notify, pushUndo, removeCropById, selectedCropIds]);

  const setRepresentative = useCallback((variantId: string, cropId: string) => {
    pushUndo(`Set rep for ${variantId.slice(0, 8)}`);
    setUpcData((prev) => {
      const next = cloneUpcs(prev);
      for (const upc of next) {
        const variant = upc.variants.find((item) => item.id === variantId);
        if (variant) {
          variant.representativeCropId = cropId;
          break;
        }
      }
      return next;
    });
    setSelectedCropIds(new Set());
    notify(`Set representative for ${variantId.slice(0, 8)}`);
  }, [notify, pushUndo]);

  const handleSectionClick = useCallback((target: SectionTarget) => {
    if (mode === 'crops-selected') {
      if (target.type === 'pending') return;
      const allAlreadyInTarget = [...selectedCropIds].every((cropId) => {
        const location = findCropLocation(cropId, upcData);
        if (!location) return false;
        const sourceItemNumber = upcData[location.upcIdx].itemNumber;
        if (sourceItemNumber !== target.itemNumber) return false;
        if (target.type === 'variant' && target.variantId && location.section === 'variant') {
          return upcData[location.upcIdx].variants[location.variantIdx].id === target.variantId;
        }
        return (
          (target.type === 'no-variant' && location.section === 'no-variant') ||
          (target.type === 'non-front' && location.section === 'non-front') ||
          (target.type === 'carton' && location.section === 'carton') ||
          (target.type === 'inactive' && location.section === 'inactive')
        );
      });
      if (allAlreadyInTarget) {
        setSelectedCropIds(new Set());
        return;
      }
      moveSelectedCrops(target);
      return;
    }

    if (mode === 'idle') {
      const item = upcData.find((entry) => entry.itemNumber === target.itemNumber);
      if (!item) return;
      let cropIds: string[] = [];
      if (target.type === 'variant' && target.variantId) {
        cropIds = item.variants.find((variant) => variant.id === target.variantId)?.crops.map((crop) => crop.id) ?? [];
      } else if (target.type === 'pending') {
        cropIds = (item.pendingCrops ?? []).map((crop) => crop.id);
      } else if (target.type === 'no-variant') {
        cropIds = item.unassignedCrops.map((crop) => crop.id);
      } else if (target.type === 'non-front') {
        cropIds = item.nonFrontCrops.map((crop) => crop.id);
      } else if (target.type === 'carton') {
        cropIds = item.cartonCrops.map((crop) => crop.id);
      } else if (target.type === 'inactive') {
        cropIds = (item.inactiveCrops ?? []).map((crop) => crop.id);
      }
      if (cropIds.length > 0) setSelectedCropIds(new Set(cropIds));
    }
  }, [findCropLocation, mode, moveSelectedCrops, selectedCropIds, upcData]);

  const getGroupCropIds = useCallback((cropId: string): string[] => {
    for (const upc of upcData) {
      for (const variant of upc.variants) {
        if (variant.crops.some((crop) => crop.id === cropId)) return sortCrops(variant.crops).map((crop) => crop.id);
      }
      if (upc.pendingCrops?.some((crop) => crop.id === cropId)) return sortCrops(upc.pendingCrops).map((crop) => crop.id);
      if (upc.unassignedCrops.some((crop) => crop.id === cropId)) return sortCrops(upc.unassignedCrops).map((crop) => crop.id);
      if (upc.nonFrontCrops.some((crop) => crop.id === cropId)) return sortCrops(upc.nonFrontCrops).map((crop) => crop.id);
      if (upc.cartonCrops.some((crop) => crop.id === cropId)) return sortCrops(upc.cartonCrops).map((crop) => crop.id);
      if (upc.inactiveCrops?.some((crop) => crop.id === cropId)) return sortCrops(upc.inactiveCrops).map((crop) => crop.id);
    }
    return [];
  }, [upcData]);

  const toggleCropSelection = useCallback((cropId: string, shiftKey = false) => {
    if (shiftKey && lastClickedCropRef.current && lastClickedCropRef.current !== cropId) {
      const groupCropIds = getGroupCropIds(cropId);
      const lastIdx = groupCropIds.indexOf(lastClickedCropRef.current);
      const currentIdx = groupCropIds.indexOf(cropId);
      if (lastIdx !== -1 && currentIdx !== -1) {
        const start = Math.min(lastIdx, currentIdx);
        const end = Math.max(lastIdx, currentIdx);
        const range = groupCropIds.slice(start, end + 1);
        setSelectedCropIds((prev) => {
          const next = new Set(prev);
          for (const id of range) next.add(id);
          return next;
        });
        lastClickedCropRef.current = cropId;
        return;
      }
    }
    lastClickedCropRef.current = cropId;
    setSelectedCropIds((prev) => {
      const next = new Set(prev);
      if (next.has(cropId)) next.delete(cropId);
      else next.add(cropId);
      return next;
    });
  }, [getGroupCropIds]);

  const selectedCropVariantInfo: SelectedCropVariantInfo | null = useMemo(() => {
    if (selectedCropIds.size !== 1) return null;
    const cropId = [...selectedCropIds][0];
    for (const upc of upcData) {
      for (const variant of upc.variants) {
        if (variant.crops.some((crop) => crop.id === cropId)) {
          return {
            variantId: variant.id,
            cropId,
            isAlreadyRep: variant.representativeCropId === cropId,
          };
        }
      }
    }
    return null;
  }, [selectedCropIds, upcData]);

  const selectedCropItemNumber = useMemo(() => {
    if (selectedCropIds.size === 0) return null;
    const upcs = new Set<string>();
    for (const cropId of selectedCropIds) {
      const location = findCropLocation(cropId, upcData);
      if (location) upcs.add(upcData[location.upcIdx].itemNumber);
    }
    return upcs.size === 1 ? [...upcs][0] : null;
  }, [findCropLocation, selectedCropIds, upcData]);

  const replaceAllUpcs = useCallback((nextUpcs: MasterUPCData[]) => {
    const cloned = cloneUpcs(nextUpcs);
    setBaseUpcData(cloned);
    setUpcData(cloneUpcs(cloned));
    setSelectedCropIds(new Set());
    setActiveVariantId(null);
    setUndoStack([]);
  }, []);

  const appendLoadedUpc = useCallback((nextUpc: MasterUPCData) => {
    const cloned = structuredClone(nextUpc);
    setBaseUpcData((prev) => [...prev.filter((item) => item.itemNumber !== cloned.itemNumber), structuredClone(cloned)]);
    setUpcData((prev) => [...prev.filter((item) => item.itemNumber !== cloned.itemNumber), cloned]);
  }, []);

  const patchUpcMetadata = useCallback((itemNumber: string, updater: (current: MasterUPCData) => MasterUPCData) => {
    setBaseUpcData((prev) => prev.map((item) => (item.itemNumber === itemNumber ? updater(item) : item)));
    setUpcData((prev) => prev.map((item) => (item.itemNumber === itemNumber ? updater(item) : item)));
  }, []);

  const removeUpc = useCallback((itemNumber: string) => {
    setBaseUpcData((prev) => prev.filter((item) => item.itemNumber !== itemNumber));
    setUpcData((prev) => prev.filter((item) => item.itemNumber !== itemNumber));
    setSelectedCropIds(new Set());
    setActiveVariantId(null);
  }, []);

  const rebaseUpcs = useCallback((freshUpcs: MasterUPCData[]) => {
    const freshByUpc = new Map(freshUpcs.map((upc) => [upc.itemNumber, structuredClone(upc)]));
    setBaseUpcData((prev) => prev.map((item) => freshByUpc.get(item.itemNumber) ?? item));
    setUpcData((prev) => prev.map((item) => structuredClone(freshByUpc.get(item.itemNumber) ?? item)));
    setSelectedCropIds(new Set());
    setActiveVariantId(null);
    setUndoStack([]);
  }, []);

  const upcHasChanges = useCallback((itemNumber: string) => diffSummary.dirtyItemNumbers.includes(itemNumber), [diffSummary.dirtyItemNumbers]);

  return {
    baseUpcData,
    upcData,
    selectedCropIds,
    setSelectedCropIds,
    toggleCropSelection,
    activeVariantId,
    setActiveVariantId,
    mode,
    variantColorMap,
    diffSummary,
    upcHasChanges,
    undoStack,
    undo,
    deactivateCrops,
    markDifferentItem,
    createNewVariant,
    setRepresentative,
    handleSectionClick,
    selectedCropVariantInfo,
    selectedCropItemNumber,
    replaceAllUpcs,
    appendLoadedUpc,
    patchUpcMetadata,
    removeUpc,
    rebaseUpcs,
    recentlyMovedCropIds,
  };
}
